import React, { useState, useMemo, useCallback } from 'react';
import { Table as LegoTable } from '@lucid/lego';
import { createColumns, Claim } from './utils';
import Details, { ClaimDetail } from './Details/Details';

type TableProps = {
  theme: 'light' | 'dark';
  data: Claim[];
};

const Table: React.FC<TableProps> = ({ theme, data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedClaimDetail, setSelectedClaimDetail] = useState<ClaimDetail | null>(null);

  const handleViewClick = useCallback(async (_claim: Claim) => {
    try {
      // Randomly select one of the 10 claim detail files
      const randomClaimNumber = String(Math.floor(Math.random() * 10) + 1).padStart(3, '0');
      const claimDetail = await import(
        `../../../../data/claimsCenter/claim-${randomClaimNumber}.json`
      );
      setSelectedClaimDetail(claimDetail.default);
      setIsSliderOpen(true);
    } catch (error) {
      console.error('Error loading claim details:', error);
    }
  }, []);

  const handleCloseSlider = () => {
    setIsSliderOpen(false);
    setSelectedClaimDetail(null);
  };

  const columns = useMemo(
    () => createColumns({ theme, onViewClick: handleViewClick }),
    [theme, handleViewClick]
  );

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase();
    return data.filter(
      (claim) =>
        claim.claimId.toLowerCase().includes(query) ||
        claim.vin.toLowerCase().includes(query) ||
        claim.lucidId.toLowerCase().includes(query) ||
        claim.claimType.toLowerCase().includes(query) ||
        claim.status.toLowerCase().includes(query) ||
        claim.reviewer.toLowerCase().includes(query)
    );
  }, [data, searchQuery]);

  return (
    <>
      <LegoTable
        columns={columns as never}
        data={filteredData as never[]}
        theme={theme === 'light' ? 'dark' : 'light'}
        pagination={{
          enabled: true,
          pageSize,
          onPageSizeChange: setPageSize,
        }}
        search={{
          placeholder: 'Search by Claim ID, VIN, Lucid ID, Type, Status, or Reviewer',
          value: searchQuery,
          onChange: setSearchQuery,
        }}
        emptyState={{
          message: searchQuery.trim() ? 'No results found' : 'No claims available',
        }}
        gaComponent="track-claim-table"
        dataTestId="track-claim-table"
        columnConfig={{ enabled: true }}
        selection={{ enabled: false }}
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={handleCloseSlider}
        claimDetail={selectedClaimDetail}
      />
    </>
  );
};

export default Table;
